import { Accommodation } from "../../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import { ExtraLegacyEntity } from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { Package } from "../../../../gen/witbooking/backoffice/package/v1/package_api_pb";
import { Tag, TagAppliesToType, TickerWithPosition } from "../../domain/Tag";
import { TagAssociation } from "../../domain/TagAssociation";
import { Translation } from "../../domain/Translation";

type GrpcTranslation = {
  getLocale(): string;
  getName(): string;
};

export const toTagAssociationFromProperty = (
  propertyTicker: string,
  tags: Tag[]
): TagAssociation => {
  const tagsByProperty = getTagsByElement(tags, "property");

  return TagAssociation.of(
    propertyTicker,
    [Translation.of("es", propertyTicker), Translation.of("en", propertyTicker)],
    "property",
    tagsByProperty.get(propertyTicker)
  );
};

export const toTagAssociationFromAccommodationGrpc = (
  accommodations: Accommodation[],
  tags: Tag[]
): TagAssociation[] => {
  const tagsByAccommodations = getTagsByElement(tags, "accommodation");

  return accommodations.map(accommodation => {
    return TagAssociation.of(
      accommodation.getTicker(),
      toTranslationsFromGrpcTranslations(accommodation.getTranslationsList()),
      "accommodation",
      tagsByAccommodations.get(accommodation.getTicker())
    );
  });
};

export const toTagAssociationFromExtrasGrpc = (extras: ExtraLegacyEntity[], tags: Tag[]): TagAssociation[] => {
  const tagsByExtras = getTagsByElement(tags, "extra");

  return extras.map(extra => {
    return TagAssociation.of(
      extra.getTicker(),
      [Translation.of("es", extra.getName()), Translation.of("en", extra.getName())], // TODO - change when extras have translations
      "extra",
      tagsByExtras.get(extra.getTicker())
    );
  });
};

export const toTagAssociationFromPackagesGrpc = (
  packages: Package[],
  tags: Tag[]
): TagAssociation[] => {
  const tagsByPackages = getTagsByElement(tags, "package");

  return packages.map(pkg => {
    return TagAssociation.of(
      pkg.getTicker(),
      toTranslationsFromGrpcTranslations(pkg.getTranslationsList()),
      "package",
      tagsByPackages.get(pkg.getTicker())
    );
  });
};

const getTagsByElement = (tags: Tag[], type: TagAppliesToType): Map<string, Tag[]> => {
  const tagsByElementWithPosition = tags
    .map(tag => {
      const appliesTo = tag.appliesTo.find(at => at.type === type);
      return [tag, appliesTo?.tickers ?? []] as [Tag, TickerWithPosition[]];
    })
    .reduce((acc, [tag, tickers]) => {
      tickers.forEach(it => {
        const tagsByTicker = acc.get(it.ticker) ?? [];
        tagsByTicker.push([it.position, tag]);
        acc.set(it.ticker, tagsByTicker);
      });
      return acc;
    }, new Map<string, [number, Tag][]>());

  const tagsByElementOrdered = new Map<string, Tag[]>();
  tagsByElementWithPosition.forEach((tagsWithPosition, ticker) => {
    const sortedTags = tagsWithPosition
      .sort(([position1], [position2]) => position1 - position2)
      .map(([position, tag]) => tag);
    tagsByElementOrdered.set(ticker, sortedTags);
  });

  return tagsByElementOrdered;
};

const toTranslationsFromGrpcTranslations = (translations: GrpcTranslation[]) => {
  return translations.map(translation =>
    Translation.of(translation.getLocale(), translation.getName())
  );
};
